import React, { useState, useId, useEffect, useRef, useMemo } from 'react'
import Select from 'components/QuestionnaireRequest/AdminNew/components/Select'
import CurrencyInput from 'react-currency-input-field'
import { useForm, Controller } from 'react-hook-form'
import { nanoid } from '@reduxjs/toolkit'
import axios from 'lib/axios'
import clsx from 'clsx'
import { pick } from 'lodash-es'
import { isSameData, itemKey, updateUrl } from 'components/QuestionnaireRequest/Property/Tabs/Pricing/utilities'

const FORM_TYPES = [
  { 'value': 'generic', 'label': 'generic' },
  { 'value': 'lender', 'label': 'lender' },
  { 'value': 'attorney', 'label': 'attorney / due dilligence' },
  { 'value': 'appraiser', 'label': 'appraiser' },
  { 'value': 'other', 'label': 'other' },
]

const LabelField = ({ register, error, watch, disabled }) => {
  const isDefault = watch('default')
  const nameRegister = register('name', {
    required: 'Label is required',
    validate: {
      notEmpty: (value) => value.trim() !== '' || 'Label cannot be empty',
    },
  })
  const onChange = (e) => {
    nameRegister.onChange(e)
  }
  return (
    <div className="questionnaire-admin-pricing-input-container">
      <input type="text" {...nameRegister} onChange={onChange} readOnly={isDefault || disabled} />
      {error && <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{error.message}</div>}
    </div>
  )
}

const MoneyField = ({ control, register, watch, error, trigger, disabled }) => {
  const checkboxId = useId()
  const dynamicPrice = watch('dynamic_price')

  const dynamicPriceRegister = register('dynamic_price')

  return (
    <div className="questionnaire-admin-pricing-input-container">
      <div className="dome-d-flex dome-justify-between dome-gap3">
        <Controller
          name="price"
          control={control}
          rules={{
            validate: {
              custom: (value) => {
                if (!dynamicPrice && !value) {
                  return 'Price is required when not variable'
                }
                if (!dynamicPrice && value && !String(value).match(/^\d+(\.\d{1,2})?$/)) {
                  return 'Invalid price format'
                }
                return true
              },
            },
          }}
          render={({ field: { onChange, value, onBlur } }) => (
            <CurrencyInput
              placeholder="$"
              defaultValue={value}
              allowDecimals={false}
              decimalScale={0}
              step={1}
              allowNegativeValue={false}
              prefix="$"
              onValueChange={(value) => {
                onChange(value)
              }}
              disabled={dynamicPrice || disabled}
              onBlur={onBlur}
            />
          )}
        />

        <div className="dome-d-flex dome-gap3 dome-align-center" style={{ flexShrink: 0 }}>
          <input
            type="checkbox"
            id={checkboxId}
            style={{ margin: 0 }}
            {...dynamicPriceRegister}
            onChange={(e) => {
              dynamicPriceRegister.onChange(e)
              setTimeout(() => trigger('price'), 1)
            }}
            disabled={disabled}
          />
          <label htmlFor={checkboxId} style={{ margin: 0 }}>
            variable
          </label>
        </div>
      </div>
      {error && <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{error.message}</div>}
    </div>
  )
}

const TableHead = () => {
  return (
    <thead>
      <tr>
        <th className="col-xs-3">
          <span>price label</span>
        </th>
        <th className="col-xs-3">
          <span>price</span>
        </th>
        <th className="col-xs-3">
          <span>type</span>
        </th>
        <th className="col-xs-3">
          <span>actions</span>
        </th>
      </tr>
    </thead>
  )
}

const prepareData = (item, formData, dirtyFields) => {
  const data = pick(formData, dirtyFields)

  if (item.is_org || item.org_questionnaire_price)
    for (let attr in data) if (!data[attr] || isSameData(data[attr], item[attr])) delete data[attr]

  if (typeof data.dynamic_price != 'undefined')
    data.dynamic_price = data.dynamic_price ? 1 : 0

  if (data.dynamic_price) delete data.price

  return data
}

const PriceItemRow = ({ price, buildingId, onUpdate, onRemove }) => {
  const [isSaving, setIsSaving] = useState(false)

  const { register, control, handleSubmit, watch, formState, setError, clearErrors, trigger, reset, getValues } =
    useForm({
      defaultValues: { ...price, default: price.default || price.org_questionnaire_price?.default || false },
      mode: 'onChange',
    })

  const { errors } = formState
  const dirtyFields = useMemo(() => {
    const { dirtyFields: dirtyFieldsObject } = formState

    // Though all values of dirtyFieldsObject are true, we should probably check if they are true in case react hook form would change the behavior
    return Object.keys(dirtyFieldsObject).filter((key) => dirtyFieldsObject[key] === true)
  }, [formState])

  const watchValues = watch()
  const isDirty = useMemo(() => {
    for (const field of dirtyFields) {
      const currentValue = getValues(field)
      const defaultValue = price[field]

      if (!isSameData(currentValue, defaultValue)) {
        return true
      }
    }
    return false
  }, [watchValues, dirtyFields, getValues])

  useEffect(() => {
    reset({
      ...pick(price, ['name', 'price', 'dynamic_price', 'selected_form_types']),
      default: price.default || price.org_questionnaire_price?.default || false,
    })
  }, [price, reset])

  const isDefault = watch('default')

  const onSubmit = async (formData) => {
    setIsSaving(true)
    try {
      const data = prepareData(price, formData, dirtyFields)
      if (price.is_org) data.org_questionnaire_price_id = price.id
      const shouldCreateItem = price.is_new || price.is_org

      const payload = {
        questionnaire_price: data,
      }
      const url = updateUrl(price, buildingId)

      let response
      if (shouldCreateItem) {
        response = await axios.post(url, payload)
        onUpdate(itemKey(price), {
          ...response.data,
          is_new: false,
          default: response.data?.org_questionnaire_price?.default || false,
        })
      } else {
        response = await axios.put(url, payload)
        onUpdate(itemKey(price), {
          ...price,
          ...response.data,
          default: response.data?.org_questionnaire_price?.default || false,
        })
      }
    } catch (error) {
      console.error('Error saving price:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleRemove = async () => {
    const disabling = price.is_org || price.org_questionnaire_price

    const priceName = getValues('name')
    const confirmText = priceName
      ? `Please confirm you wish to remove the following type: ${priceName}`
      : 'Please confirm you wish to remove the type'

    const swalOptions = {
      type: 'warning',
      title: disabling ? 'Disable Type' : 'Remove Type',
      text: confirmText,
      showCancelButton: true,
    }

    const data = {}
    if (price.is_org) data.org_questionnaire_price_id = price.id

    const url = updateUrl(price, buildingId)

    window.swal(swalOptions, async (confirm) => {
      if (!confirm) return

      if (price.is_new) {
        onRemove(itemKey(price))
        return
      }

      try {
        setIsSaving(true)
        const response = await axios.delete(url, { data: { questionnaire_price: data } })
        if (response.data && response.data.id) {
          onUpdate(itemKey(price), response.data)
        } else if (disabling) {
          onUpdate(itemKey(price), { ...price, disabled: true })
        } else {
          onRemove(itemKey(price))
        }
      } catch (error) {
        console.error('Error removing price:', error)
      } finally {
        setIsSaving(false)
      }
    })
  }

  const handleRestore = async () => {
    const swalOptions = {
      type: 'info',
      title: 'Restore Type',
      text: 'Restore this type for this building',
      showCancelButton: true,
    }

    const url = updateUrl(price, buildingId) + '/restore'

    window.swal(swalOptions, async (confirm) => {
      if (!confirm) return

      try {
        setIsSaving(true)
        await axios.put(url, {})
        onUpdate(itemKey(price), { ...price, disabled: false })
      } catch (error) {
        console.error('Error restoring price:', error)
      } finally {
        setIsSaving(false)
      }
    })
  }

  const handleFormTypesChange = (selectedItems) => {
    if (!selectedItems || selectedItems.length === 0) {
      setError('selected_form_types', {
        type: 'manual',
        message: 'At least one type must be selected',
      })
    } else {
      clearErrors('selected_form_types')
    }
  }

  const hasErrors = Object.keys(errors).length > 0
  const isDisabled = price.disabled || !isDirty || hasErrors || isSaving

  return (
    <tr>
      <td>
        <LabelField
          register={register}
          watch={watch}
          error={errors.name}
          disabled={isDefault || price.disabled || isSaving}
        />
      </td>
      <td>
        <MoneyField
          control={control}
          register={register}
          watch={watch}
          trigger={trigger}
          error={errors.price}
          disabled={isDefault || price.disabled || isSaving}
        />
      </td>
      <td>
        <div>
          <Controller
            name="selected_form_types"
            control={control}
            rules={{
              validate: (value) => (value && value.length > 0) || 'At least one type must be selected',
            }}
            render={({ field: { onChange, value, onBlur } }) => (
              <Select
                multiple={true}
                options={FORM_TYPES}
                defaultSelectedItems={value}
                onChange={(selectedItems) => {
                  onChange(selectedItems)
                  handleFormTypesChange(selectedItems)
                }}
                onClose={() => onBlur()}
                selectAll={true}
                selectAllOptionLabel="select all types"
                selectAllDisplay="all types"
                label="select type"
                disabled={isDefault || price.disabled}
              />
            )}
          />
          {errors.selected_form_types && (
            <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{errors.selected_form_types.message}</div>
          )}
        </div>
      </td>
      <td>
        <div className="dome-d-flex dome-gap3">
          {price.disabled ? (
            <button
              className={clsx(
                `dome-btn dome-btn-base dome-btn-aqua-stroke`,
                (isDefault || isSaving) && 'dome-btn-disabled'
              )}
              onClick={handleRestore}
              disabled={isDefault || isSaving}
            >
              restore
            </button>
          ) : (
            <>
              <button
                className={clsx(`dome-btn dome-btn-base dome-btn-go-green`, isDisabled && 'dome-btn-disabled')}
                onClick={handleSubmit(onSubmit)}
                disabled={isDefault || isDisabled}
              >
                save
              </button>
              <button
                className={clsx(
                  `dome-btn dome-btn-base dome-btn-red-stroke`,
                  (isDefault || isSaving) && 'dome-btn-disabled'
                )}
                onClick={handleRemove}
                disabled={isDefault || isSaving}
              >
                {price.is_org || price.org_questionnaire_price ? 'disable' : 'remove'}
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  )
}

const TableBody = ({ prices, buildingId, onUpdate, onRemove, disabled = false }) => {
  if (prices.length === 0 && !disabled) {
    return (
      <tbody>
        <tr>
          <td colSpan={4}>Click "+ price" to create a price</td>
        </tr>
      </tbody>
    )
  }

  return (
    <tbody>
      {prices.map((price) => (
        <PriceItemRow
          key={itemKey(price)}
          price={price}
          buildingId={buildingId}
          onUpdate={onUpdate}
          onRemove={onRemove}
        />
      ))}
    </tbody>
  )
}

export default function Pricing({ buildingId, prices, onAddNewPrice, onUpdatePrice, onRemovePrice, disabled = false }) {
  const addNewPrice = () => {
    const newPrice = {
      id: nanoid(),
      is_new: true,
      name: '',
      price: null,
      dynamic_price: false,
      selected_form_types: FORM_TYPES.map((item) => item.value),
      default: false,
    }
    onAddNewPrice(newPrice)
  }

  const handleUpdatePrice = (oldKey, updatedPrice) => {
    onUpdatePrice({ oldKey, updatedPrice })
  }

  const handleRemovePrice = (priceKey) => {
    onRemovePrice(priceKey)
  }

  return (
    <>
      <table className="dome-table dome-table-striped dome-full-width">
        <TableHead />
        <TableBody
          prices={prices}
          buildingId={buildingId}
          onUpdate={handleUpdatePrice}
          onRemove={handleRemovePrice}
          disabled={disabled}
        />
      </table>
      {!disabled && (
        <div className="dome-d-flex dome-justify-between">
          <button type="button" className="dome-btn dome-btn-base dome-btn-aqua-stroke" onClick={addNewPrice}>
            + price
          </button>
        </div>
      )}
    </>
  )
}
